
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import globalLink from "../Assets/giiconn-2.png";
import globalLink2 from "../Assets/giiconn-3.png";
import globalLink4 from "../Assets/giicon-7.png";
import { amplitude } from "../../analytics";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
  success: "#0A7D34",
  successSoft: "#D1FAE5",
};

const MENTOR_EMAIL = "info@giiconnect.ai";

const signupImages = [globalLink, globalLink2, globalLink4];
const randomImage = signupImages[Math.floor(Math.random() * signupImages.length)];

const getMaxDob = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Simple LinkedIn URL sanity check — accepts linkedin.com/in/... in any common shape
const isLinkedInUrl = (url) => {
  if (!url) return false;
  return /(^https?:\/\/)?(www\.|[a-z]{2}\.)?linkedin\.com\/(in|pub)\/[\w\-_%]+\/?/i.test(url.trim());
};

// ── Mentor Interest Modal ────────────────────────────────────────────────────
function MentorInterestModal({ open, onClose }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [intro, setIntro] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const resetAndClose = () => {
    setFullName(""); setEmail(""); setLinkedinUrl(""); setIntro("");
    setError(""); setSent(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!fullName.trim() || !email.trim() || !linkedinUrl.trim()) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!isLinkedInUrl(linkedinUrl)) {
      setError("Please enter a valid LinkedIn profile URL (linkedin.com/in/...)");
      return;
    }

    setSubmitting(true);

    // ── DEFAULT: mailto fallback (works without a backend endpoint) ──────
    // When you build a backend endpoint, replace this block with:
    //
    //   await fetch(`${API}/api/mentor-interest`, {
    //     method: "POST",
    //     headers: { "Content-Type": "application/json" },
    //     body: JSON.stringify({ fullName, email, linkedinUrl, intro }),
    //   });
    //
    // The backend handler would send an email to info@giiconnect.ai via Resend
    // and respond 200. For now the mailto opens the user's mail client with a
    // pre-filled draft they only need to click Send on.

    const subject = encodeURIComponent("Mentor Application — giiconnect");
    const body = encodeURIComponent(
      `Hi giiconnect team,

I'd like to apply to be a mentor on giiconnect.

Name: ${fullName}
Email: ${email}
LinkedIn: ${linkedinUrl}

${intro ? `About me:
${intro}

` : ""}Looking forward to hearing from you.

Best regards,
${fullName}`
    );

    // Open the user's email client with the draft
    window.location.href = `mailto:${MENTOR_EMAIL}?subject=${subject}&body=${body}`;

    // Brief delay so the mail client has a moment to launch
    setTimeout(() => {
      setSent(true);
      setSubmitting(false);
    }, 400);
  };

  return (
    <div
      onClick={resetAndClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1500,
        background: "rgba(13,17,23,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        animation: "fadeIn 0.2s ease",
      }}
    >
      {/* Card — stopPropagation so clicks inside don't close */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto",
          background: C.paper,
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(13,17,23,0.25)",
          position: "relative",
          animation: "slideUp 0.25s ease",
        }}
      >
        {/* Gradient header strip */}
        <div style={{
          position: "relative",
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
          padding: "24px 28px 22px",
          color: "#fff",
          borderTopLeftRadius: 16, borderTopRightRadius: 16,
          overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: "-60%", right: "-15%",
            width: 200, height: 200, borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}55 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          {/* Close button */}
          <button
            type="button"
            onClick={resetAndClose}
            aria-label="Close"
            style={{
              position: "absolute", top: 14, right: 14,
              background: "rgba(255,255,255,0.18)",
              border: "none", borderRadius: 8,
              width: 30, height: 30,
              color: "#fff", cursor: "pointer",
              fontSize: 18, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(4px)",
              zIndex: 1,
            }}
          >
            ×
          </button>

          <div style={{ position: "relative", zIndex: 1 }}>
            <p style={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: 1.5,
              textTransform: "uppercase", color: "rgba(255,255,255,0.7)",
              margin: 0, marginBottom: 6,
            }}>
              Mentor Application
            </p>
            <h2 style={{
              fontSize: "1.4rem", fontWeight: 800, margin: 0,
              letterSpacing: -0.3,
            }}>
              Apply to Mentor
            </h2>
            <p style={{
              color: "rgba(255,255,255,0.85)",
              fontSize: 13, margin: 0, marginTop: 6, lineHeight: 1.5,
            }}>
              Share your details — our team reviews every application personally.
            </p>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 28px 28px" }}>
          {sent ? (
            // Success state
            <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%",
                background: `linear-gradient(135deg, ${C.secondary}, ${C.secondaryDark})`,
                margin: "0 auto 16px",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 30, fontWeight: 800,
                boxShadow: `0 6px 16px ${C.secondary}40`,
              }}>
                ✓
              </div>
              <h3 style={{
                fontSize: 17, fontWeight: 800, color: C.textPrimary,
                margin: 0, marginBottom: 8,
              }}>
                Email draft opened
              </h3>
              <p style={{
                fontSize: 13.5, color: C.textSecondary, lineHeight: 1.6,
                margin: 0, marginBottom: 20,
              }}>
                Your default mail client should have opened with your details pre-filled.
                Click <strong style={{ color: C.textPrimary }}>Send</strong> in your email
                client to complete your application.
              </p>
              <p style={{
                fontSize: 12, color: C.textMuted, marginBottom: 20,
              }}>
                Didn't see it? Email{" "}
                <a
                  href={`mailto:${MENTOR_EMAIL}`}
                  style={{ color: C.primary, fontWeight: 700, textDecoration: "none" }}
                >
                  {MENTOR_EMAIL}
                </a>{" "}
                directly.
              </p>
              <button
                type="button"
                onClick={resetAndClose}
                style={{
                  background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                  border: "none", borderRadius: 10,
                  padding: "12px 28px", color: "#fff",
                  fontWeight: 700, fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <FieldLabel label="Full Name *" />
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                style={inputStyle}
                required
                autoFocus
              />

              <FieldLabel label="Official Email *" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={inputStyle}
                required
              />

              <FieldLabel label="LinkedIn Profile URL *" />
              <input
                type="url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/your-handle"
                style={inputStyle}
                required
              />

              <FieldLabel label="Brief Introduction (optional)" />
              <textarea
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                placeholder="A few words about your background and what you'd like to mentor on…"
                rows={3}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
              />

              {error && (
                <div style={{
                  color: C.error, fontSize: 13, fontWeight: 600,
                  background: C.errorSoft, padding: "10px 12px",
                  borderRadius: 8, textAlign: "center",
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                <button
                  type="button"
                  onClick={resetAndClose}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    background: C.paper,
                    border: `1.5px solid ${C.border}`,
                    borderRadius: 10,
                    padding: "12px",
                    color: C.textSecondary,
                    fontWeight: 700, fontSize: 14,
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    flex: 2,
                    background: submitting
                      ? "#CCC"
                      : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                    border: "none",
                    borderRadius: 10,
                    padding: "12px",
                    color: "#fff",
                    fontWeight: 700, fontSize: 14,
                    cursor: submitting ? "not-allowed" : "pointer",
                    boxShadow: submitting ? "none" : `0 4px 14px ${C.primary}30`,
                  }}
                >
                  {submitting ? "Opening…" : "Send Application"}
                </button>
              </div>

              <p style={{
                fontSize: 11, color: C.textMuted, textAlign: "center",
                margin: 0, marginTop: 4, lineHeight: 1.5,
              }}>
                Submitting opens your email client with a pre-filled draft. We typically respond within 3–5 business days.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
    </div>
  );
}

const FieldLabel = ({ label }) => (
  <label style={{
    fontSize: 12, fontWeight: 700, color: C.textSecondary,
    marginBottom: -8, paddingLeft: 2,
  }}>
    {label}
  </label>
);

// ── Main Signup ──────────────────────────────────────────────────────────────
function Signup() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [mentorModalOpen, setMentorModalOpen] = useState(false);

  const maxDob = useMemo(() => getMaxDob(), []);

  const isAtLeast18 = (dobStr) => {
    if (!dobStr) return false;
    const dob = new Date(dobStr);
    if (isNaN(dob.getTime())) return false;
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 18);
    return dob <= cutoff;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    if (!dateOfBirth) { setError("Please enter your date of birth"); return; }
    if (!isAtLeast18(dateOfBirth)) { setError("You must be at least 18 years old to sign up"); return; }
    if (!acceptedTerms) { setError("You must accept the Terms & Conditions"); return; }

    const payload = { fullName, email, location, dateOfBirth, password, confirmPassword };

    try {
      setLoading(true);
      const res = await fetch("https://backend-global-1vq9.onrender.com/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || data.message || "Signup failed");

      // Track BEFORE navigating away, as its own statement
      amplitude.track('Signup Completed', { email });

      navigate("/login", {
        state: {
          notice: data.message || "Account created. Check your email to verify your account before logging in.",
        },
      },
  );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: C.bg }}>
      {/* HEADER */}
      <div style={{
        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
        padding: "80px 20px 50px",
        textAlign: "center",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: "-40%", right: "-10%",
          width: 280, height: 280, borderRadius: "50%",
          background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <p style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
            textTransform: "uppercase", color: "rgba(255,255,255,0.7)",
            marginBottom: 8,
          }}>
            Join the Community
          </p>
          <h1 style={{ fontSize: "2.25rem", fontWeight: 800, marginBottom: 12, letterSpacing: -0.5 }}>
            Welcome
          </h1>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1rem", maxWidth: 500, margin: "0 auto", lineHeight: 1.5 }}>
            Access your network, connect with mentors, and collaborate with a thriving community.
          </p>
        </div>
      </div>

      {/* FORM SECTION */}
      <form
        onSubmit={handleSignup}
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 40,
          padding: "32px 20px",
          flex: 1,
        }}
      >
        <img src={randomImage} alt="illustration" style={{ width: 400, maxWidth: "100%", height: "100%" }} />

        <div style={{
          flex: "1 1 350px",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          background: C.paper,
          padding: 28,
          borderRadius: 16,
          border: `1px solid ${C.border}`,
          boxShadow: "0 4px 24px rgba(13,17,23,0.06)",
          alignSelf: "flex-start",
        }}>

          <input
            type="text" placeholder="Full Name" value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            style={inputStyle} required
          />

          <input
            type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle} required
          />

          <input
            type="text" placeholder="Location" value={location}
            onChange={(e) => setLocation(e.target.value)}
            style={inputStyle} required
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label htmlFor="dateOfBirth" style={{ fontSize: 12, color: C.textSecondary, paddingLeft: 2, fontWeight: 600 }}>
              Date of Birth (must be 18+)
            </label>
            <input
              id="dateOfBirth" type="date" value={dateOfBirth} max={maxDob}
              onChange={(e) => setDateOfBirth(e.target.value)}
              style={inputStyle} required
            />
          </div>

          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle} required
          />

          <input
            type={showPassword ? "text" : "password"}
            placeholder="Confirm Password" value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={inputStyle} required
          />

          <span
            onClick={() => setShowPassword(!showPassword)}
            style={{ fontSize: 12, color: C.primary, cursor: "pointer", textAlign: "right", fontWeight: 600 }}
          >
            {showPassword ? "Hide" : "Show"} Password
          </span>

          <div style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            fontSize: 14, color: C.textSecondary, lineHeight: 1.6,
          }}>
            <input
              type="checkbox" checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{ marginTop: 4, cursor: "pointer", accentColor: C.primary }}
            />
            <p style={{ margin: 0 }}>
              I agree to the{" "}
              <span
                onClick={() => navigate("/termsOfservice")}
                style={{ color: C.primary, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
              >
                Terms & Conditions
              </span>
            </p>
          </div>

          {error && (
            <div style={{
              color: C.error, fontSize: 13, textAlign: "center",
              background: C.errorSoft, padding: 10, borderRadius: 8, fontWeight: 600,
            }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={loading || !acceptedTerms}
            style={{
              background: loading || !acceptedTerms
                ? "#CCCCCC"
                : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              border: "none", borderRadius: 10,
              padding: "14px", color: "#fff",
              fontWeight: 700, fontSize: 15,
              cursor: loading || !acceptedTerms ? "not-allowed" : "pointer",
              width: "100%",
              transition: "all 0.2s ease",
              boxShadow: loading || !acceptedTerms ? "none" : `0 4px 14px ${C.primary}30`,
            }}
          >
            {loading ? "Signing Up..." : "Sign Up"}
          </button>

          {/* Mentor note + Apply button */}
          <div style={{
            background: C.primarySofter,
            border: `1px solid ${C.primarySoft}`,
            borderRadius: 10,
            padding: "14px 16px",
            fontSize: 12.5,
            color: C.textSecondary,
            lineHeight: 1.55,
          }}>
            <strong style={{ color: C.primary, fontSize: 13 }}>Looking to mentor?</strong>
            <p style={{ margin: "4px 0 12px" }}>
              Mentors join giiconnect by invitation only. Send us your details and we'll be in touch.
            </p>
            <button
              type="button"
              onClick={() => setMentorModalOpen(true)}
              style={{
                background: C.paper,
                border: `1.5px solid ${C.primary}`,
                borderRadius: 8,
                padding: "9px 14px",
                color: C.primary,
                fontWeight: 700, fontSize: 12.5,
                cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.primarySoft;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = C.paper;
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Apply to Mentor →
            </button>
          </div>

          <p style={{ textAlign: "center", fontSize: 14, color: C.textSecondary }}>
            Already have an account?{" "}
            <span
              style={{ color: C.primary, fontWeight: 700, cursor: "pointer" }}
              onClick={() => navigate("/login")}
            >
              Sign In
            </span>
          </p>
        </div>
      </form>

      {/* Mentor application modal */}
      <MentorInterestModal
        open={mentorModalOpen}
        onClose={() => setMentorModalOpen(false)}
      />
    </div>
  );
}

const inputStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: "12px 14px",
  background: C.paper,
  fontSize: 14,
  color: C.textPrimary,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

export default Signup;